import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { AiOutlineUser, AiOutlineTeam, AiOutlineBarChart } from 'react-icons/ai';
import { FaPlusCircle } from 'react-icons/fa';
import { LuFileSpreadsheet } from 'react-icons/lu';
import { FaUserPlus, FaBoxOpen } from 'react-icons/fa';
import { FaHandHoldingUser } from 'react-icons/fa6';
import { MdOutlineCategory } from 'react-icons/md';
import { connect } from 'react-redux';
import { Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MdInsertPhoto } from 'react-icons/md';
import Logo from '../../../assets/global/logoFox.png';
import { CustomService } from '../../../services/api/custom/CustomService';
import './styles.scss';

const Layout = ({ exit, user }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation(); // Hook para obter a localização atual

  useEffect(() => {
    CustomService.getRequest('/interno/categorias')
      .then((resp) => {
        const list = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        setCategories(list);
      })
      .catch((error) => {
        console.error('Erro ao carregar categorias do menu:', error);
      });
  }, []);

  const categoryItems = (categories || []).map((cat) => ({
    key: `/categorias/${cat.id}`,
    label: cat.nome,
    icon: <FaBoxOpen />,
    onClick: () =>
      navigate(`/categorias/${cat.id}`, {
        state: { categoriaNome: cat.nome },
      }),
  }));

  const semCategoriaItem = {
    key: '/categorias/sem-categoria',
    label: 'Sem Categoria',
    icon: <FaBoxOpen />,
    onClick: () =>
      navigate('/categorias/sem-categoria', {
        state: { categoriaNome: 'Sem Categoria' },
      }),
  };

  const items = [
    {
      key: 'grp1',
      label: 'Orçamentos',
      type: 'group',
      children: [
        {
          key: 'sub1',
          label: 'Listagem',
          icon: <LuFileSpreadsheet />,
          onClick: () => navigate('/orcamentos'),
        },
        {
          key: '/relatorios',
          label: 'Relatórios',
          icon: <AiOutlineBarChart />,
          onClick: () => navigate('/relatorios'),
        },
        {
          key: 'sub2',
          label: 'Gerar',
          icon: <FaPlusCircle />,
          onClick: () => navigate('/gerar-orcamento'),
        },
      ],
    },

    {
      key: 'grp2',
      label: 'Produtos',
      type: 'group',
      children: [
        ...(categoryItems.length > 0
          ? categoryItems
          : [
              {
                key: 'categorias-empty',
                label: 'Sem categorias',
                disabled: true,
              },
            ]),
        semCategoriaItem,
        {
          key: '/segmentos',
          label: 'Segmentos',
          icon: <MdOutlineCategory />,
          onClick: () => navigate('/segmentos'),
        },
        {
          key: '/cadastro/produto',
          label: 'Adicionar Novo',
          icon: <FaPlusCircle />,
          onClick: () => navigate('/cadastro/produto'),
        },
      ],
    },

    // Mostrar apenas para admin a opção de adicionar novo colaborador
    user?.admin === true && {
      key: 'grp',
      label: 'Cadastros',
      type: 'group',
      children: [
        {
          key: 'sub7',
          label: 'Clientes',
          icon: <FaUserPlus />,
          onClick: () => navigate('/clientes'),
        },
        {
          key: 'sub8',
          label: 'Vendedores',
          icon: <AiOutlineTeam />,
          onClick: () => navigate('/vendedores'),
        },
        {
          key: 'sub9',
          label: 'Colaboradores',
          icon: <AiOutlineTeam />,
          onClick: () => navigate('/colaboradores'),
        },
      ],
    },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleExit = () => {
    exit();
  };

  // Fechar o menu ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Determinar a chave selecionada com base na localização atual
  const flatItems = items.flatMap((item) => [item, ...(item.children || [])]);
  const selectedKey =
    flatItems.find((item) => item.key === location.pathname)?.key ||
    flatItems.find((item) => location.pathname.includes(item.key))?.key;

  return (
    <div className="flex w-full relative flex-col md:flex-row">
      {/* START: MENU LATERAL */}
      <div
        ref={sidebarRef}
        className={`sidebar-container flex flex-col md:justify-between w-[100%] md:w-[280px] fixed top-0 left-0 h-[100vh] bg-blue-default z-50 transform ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out md:relative md:translate-x-0 w-[256px]`}
      >
        <div className="flex flex-col gap-2 md:gap-0">
          <div className="md:h-[60px] w-full flex flex-col justify-center items-start pl-5">
            <img
              onClick={() => navigate('/dashboard')}
              src={Logo}
              className="hidden md:flex md:w-[100px] w-[150px] hover:cursor-pointer"
              alt="logo"
            />
          </div>

          <div className="flex w-full justify-between md:hidden px-5">
            {/* <img src={Logo} className="w-[120px] curs" alt="logo" /> */}
            <button
              onClick={toggleMenu}
              className="text-[16px] text-white flex gap-2 items-center"
            >
              <CloseOutlined className="text-[15px]" />
              <span>Fechar</span>
            </button>
          </div>

          <Menu
            onClick={() => closeMenu()}
            className="custom-menu pt-2"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={['sub1']}
            mode="inline"
            items={items}
          />
        </div>
        <div className="custom-menu-config w-full">
          <ul>
            <li onClick={handleExit}>
              <LogoutOutlined />
              <span>Sair</span>
            </li>
          </ul>
        </div>
      </div>
      {/* END: MENU LATERAL */}

      {/* START: LATERAL DIREITA */}
      <div className="w-full flex flex-col">
        {/* START: MENU SUPERIOR */}
        <div className="w-full md:h-[60px] h-[50px] px-2 md:px-8 flex justify-between md:justify-end items-center bg-blue-default text-white">
          <div className="hamburger-icon md:hidden" onClick={toggleMenu}>
            {isMenuOpen ? (
              <CloseOutlined className="text-2xl" />
            ) : (
              <MenuOutlined className="text-2xl" />
            )}
          </div>
          <img
            src={Logo}
            className="md:hidden md:w-[180px] w-[80px]"
            alt="logo"
          />
          <button
            className="flex items-center gap-2"
            onClick={() => navigate('/perfil')}
          >
            <div className="w-[35px] h-[35px] rounded-full bg-white flex justify-center items-center">
              <AiOutlineUser style={{ color: 'black', fontSize: '25px' }} />
            </div>
            <div className="flex flex-col items-start">
              <span>{user?.nome}</span>
              <span className="text-[12px]">{user?.email}</span>
            </div>
          </button>
        </div>
        {/* START: MENU SUPERIOR */}

        {/* START: CONTEÚDO DA PÁGINA */}
        <main className="md:p-4 bg-[#ededed] h-[92vh] overflow-y-auto">
          <Outlet />
        </main>
        {/* END: CONTEÚDO DA PÁGINA */}
      </div>
      {/* END: LATERAL DIREITA */}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
  role: state.auth.role,
  error: state.auth.error,
});

const mapDispatchToProps = (dispatch) => ({
  exit: () => dispatch({ type: 'LOGOUT' }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
